import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Table, Badge, Button, Form, ProgressBar, Modal } from 'react-bootstrap';
import confetti from 'canvas-confetti';
import authService from '../services/authService';
import FunnelDiagram from '../components/FunnelDiagram';
import { TextWithTooltips } from '../components/Tooltip';
import { colors, gradients } from '../brand-colors';

function BattlePlan() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [battlePlan, setBattlePlan] = useState(null);
  const [capacityAnalysis, setCapacityAnalysis] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [competitiveData, setCompetitiveData] = useState(null);
  const [setupComplete, setSetupComplete] = useState({
    logo: false,
    ghl: false,
    qr: false,
    email: false
  });
  const [isQuickStartUser, setIsQuickStartUser] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [commandCenterActivated, setCommandCenterActivated] = useState(false);

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Celebrate with confetti when battle plan loads
  useEffect(() => {
    if (!loading && battlePlan) {
      // Fire confetti from both sides - brand colors (red + gold)
      const end = Date.now() + 1500;
      const colors = ['#a10000', '#ba1d1d', '#db3838', '#ddb75e', '#cca557', '#ba934f'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());
    }
  }, [loading, battlePlan]);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if quick-start user
    try {
      const statusResponse = await fetch('/api/quick-start/get-status', {
        method: 'GET',
        credentials: 'include'
      });
      if (statusResponse.ok) {
        const status = await statusResponse.json();
        setIsQuickStartUser(status.onboarding_path === 'quick_start' || status.onboarding_path === 'quick_start_exploring');
      }
    } catch (err) {
      console.error('Error checking quick-start status:', err);
    }

    // Check Command Center activation status
    try {
      const ccResponse = await fetch('/api/onboarding/command-center-status', {
        method: 'GET',
        credentials: 'include'
      });
      if (ccResponse.ok) {
        const ccStatus = await ccResponse.json();
        setCommandCenterActivated(ccStatus.activated);
      }
    } catch (err) {
      console.error('Error checking Command Center status:', err);
    }

    await Promise.all([
      fetchBattlePlan(),
      fetchCapacityAnalysis(),
      fetchFunnelData(),
      fetchCompetitiveData()
    ]);
    setLoading(false);
  };

  // Navigate to appropriate dashboard based on user type
  const handleBackToDashboard = () => {
    if (isQuickStartUser) {
      navigate('/quick-start-dashboard');
    } else {
      navigate('/dashboard/branding');
    }
  };

  const fetchFunnelData = async () => {
    try {
      console.log('[BATTLE PLAN] Fetching funnel data...');
      const response = await fetch('/api/marketing/funnel-diagram', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[BATTLE PLAN] Funnel data received:', {
          products: data?.products?.length || 0,
          upgradePaths: data?.upgrade_paths?.length || 0,
          promotions: data?.promotions?.length || 0,
          trafficSources: data?.traffic_sources?.length || 0
        });
        setFunnelData(data);
      } else {
        console.error('[BATTLE PLAN] Funnel data fetch failed:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('[BATTLE PLAN] Error fetching funnel data:', err);
    }
  };

  const handleUpgradeToCommandCenter = async () => {
    setUpgrading(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/upgrade-to-command-center', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCommandCenterActivated(true);
        setShowComingSoonModal(false);

        // Show success and redirect
        const tasksMsg = data.tasks_created > 0
          ? `\n\n${data.tasks_created} tasks from your Exit Strategy have been added to your task list.`
          : '';

        alert(`🎉 Command Center Activated!${tasksMsg}\n\nRedirecting to your Command Center...`);
        navigate('/command-center');
      } else {
        const errorData = await response.json();
        if (errorData.already_activated) {
          setCommandCenterActivated(true);
          navigate('/command-center');
        } else {
          setError(errorData.error || 'Failed to activate Command Center');
        }
      }
    } catch (err) {
      setError('Error activating Command Center: ' + err.message);
    }

    setUpgrading(false);
  };

  const fetchBattlePlan = async () => {
    try {
      const response = await fetch('/api/marketing/generate-battle-plan', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setBattlePlan(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate battle plan');
      }
    } catch (err) {
      setError('Error generating battle plan: ' + err.message);
    }
  };

  const fetchCapacityAnalysis = async () => {
    try {
      const response = await fetch('/api/marketing/marketing-capacity-analysis', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCapacityAnalysis(data);
        console.log('[BATTLE PLAN] Capacity analysis:', data.recommendation);
      } else {
        // Not an error - just means no exit strategy yet
        console.log('[BATTLE PLAN] No capacity analysis available');
      }
    } catch (err) {
      console.log('[BATTLE PLAN] Could not fetch capacity analysis:', err.message);
    }
  };

  const fetchCompetitiveData = async () => {
    try {
      const response = await fetch('/api/competitive-analysis/get-saved', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.analysis && data.analysis.length > 0) {
          setCompetitiveData(data);
        }
      }
    } catch (err) {
      console.log('[BATTLE PLAN] Could not fetch competitive data:', err.message);
    }
  };

  const handleDownloadCapacityCSV = async () => {
    try {
      const response = await fetch('/api/marketing/marketing-capacity-csv', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'marketing-capacity-analysis.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download CSV');
      }
    } catch (err) {
      console.error('Error downloading CSV:', err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getFunnelStageBadge = (stage) => {
    // Changed from TOFU/MOFU/BOFU to user-friendly terms
    const badges = {
      'tofu': { bg: 'primary', text: 'Attract' },
      'mofu': { bg: 'success', text: 'Engage' },
      'bofu': { bg: 'warning', text: 'Convert' },
      'retention': { bg: 'info', text: 'Retain' },
      'all': { bg: 'secondary', text: 'All Stages' }
    };
    const badge = badges[stage] || badges['all'];
    return <Badge bg={badge.bg} className="ms-2">{badge.text}</Badge>;
  };

  const toggleSetupItem = (item) => {
    setSetupComplete({ ...setupComplete, [item]: !setupComplete[item] });
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Generating your 90-day marketing battle plan...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={handleBackToDashboard}>← Back to Dashboard</Button>
      </Container>
    );
  }

  if (!battlePlan) {
    return null;
  }

  // Calculate precise hours and dollar value
  const preciseHours = battlePlan.monthly_output.total_estimated_hours;
  const dollarValue = Math.round(preciseHours * 45); // $45/hour

  return (
    <Container className="py-5" style={{ maxWidth: '1200px' }}>
      {/* Top navigation buttons */}
      <div className="d-flex justify-content-between mb-4">
        <Button variant="outline-secondary" onClick={handleBackToDashboard}>
          ← Back to Dashboard
        </Button>
        <Button variant="outline-primary" onClick={handlePrint}>
          🖨️ Print Battle Plan
        </Button>
      </div>

      {/* ENHANCEMENT #3: Peak-End Celebration */}
      <Alert variant="success" className="text-center mb-5 shadow-sm" style={{ border: '2px solid #198754' }}>
        <h3 className="mb-3">🎉 Congratulations on Completing Your Strategic Foundation!</h3>
        <p className="mb-0 lead">
          You've defined your vision, identified your market, built your brand identity, and mapped your exit strategy.
          <br />
          <strong>Now it's time to execute.</strong>
        </p>
      </Alert>

      {/* ENHANCEMENT #1: Loss Aversion Opening */}
      <Card className="mb-5 shadow-lg border-danger" style={{ borderWidth: '3px' }}>
        <Card.Body className="text-center py-4">
          <h2 className="text-danger fw-bold mb-3">⚠️ While You Read This Your Competitors...</h2>
          <Row className="text-center mb-3">
            <Col md={4}>
              <div className="mb-2">
                <div className="fw-bold" style={{ fontSize: '2rem', color: '#dc3545' }}>12+</div>
                <div className="text-muted">Social updates posted by your competitors <strong>today</strong></div>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-2">
                <div className="fw-bold" style={{ fontSize: '2rem', color: '#dc3545' }}>3</div>
                <div className="text-muted">Prospects just Googled your industry and <strong>found them first</strong></div>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-2">
                <div className="fw-bold" style={{ fontSize: '2rem', color: '#dc3545' }}>${dollarValue}+</div>
                <div className="text-muted">In potential revenue slipping to businesses with <strong>consistent presence</strong></div>
              </div>
            </Col>
          </Row>
          <div className="mt-4 pt-3" style={{ borderTop: '2px solid #dc3545' }}>
            <h4 className="text-dark">Your 90-Day EXIT STRATEGY Kick-Off Plan Starts Below ↓</h4>
          </div>
        </Card.Body>
      </Card>

      {/* Competitive Positioning Table - Know Your Enemy */}
      {competitiveData && competitiveData.analysis && competitiveData.analysis.length > 0 && (
        <Card className="mb-5 shadow border-warning" style={{ borderWidth: '2px' }}>
          <Card.Header className="bg-warning">
            <h3 className="mb-0">🎯 Know Your Enemy: Positioning & Pricing Comparison</h3>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              This is who you're up against. Use this to sharpen your messaging and find your angle.
            </p>
            <div className="table-responsive">
              <Table bordered hover>
                <thead className="table-dark">
                  <tr>
                    <th style={{width: '20%'}}>Company</th>
                    <th style={{width: '40%'}}>How They Position Themselves</th>
                    <th style={{width: '40%'}}>Pricing Strategy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="table-success">
                    <td><strong>YOU</strong></td>
                    <td>{competitiveData.user_positioning || <em className="text-muted">Complete competitive analysis to see your positioning</em>}</td>
                    <td>{competitiveData.user_pricing || <em className="text-muted">Not analyzed</em>}</td>
                  </tr>
                  {competitiveData.analysis.map((comp, index) => (
                    <tr key={index}>
                      <td><strong>{comp.name || `Competitor ${index + 1}`}</strong></td>
                      <td>{comp.positioning || <em className="text-muted">Not found</em>}</td>
                      <td>
                        {typeof comp.pricing === 'object' && comp.pricing.description
                          ? comp.pricing.description
                          : comp.pricing || <em className="text-muted">Not found</em>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Alert variant="info" className="mb-0 mt-3">
              <strong>Strategic Insight:</strong> Look for gaps in their positioning. Where can you be different? What are they NOT saying that you can own?
            </Alert>
          </Card.Body>
        </Card>
      )}

      {/* Header */}
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold mb-3">Your 90-Day Marketing Battle Plan</h1>
        <p className="lead text-muted mb-0">
          Stop losing ground. Start building momentum. Here's exactly what to do—week by week.
        </p>
      </div>

      {/* YOUR FIRST 30 DAYS - Funnel Visualization */}
      {(() => {
        const startDate = new Date();
        const endDate = new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000);
        const formatDate = (d) => d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        return (
          <Card className="mb-5 shadow">
            <Card.Header className="bg-primary text-white">
              <h2 className="mb-0" style={{ fontSize: '1.8rem' }}>
                Your First 30 Days ({formatDate(startDate)} - {formatDate(endDate)}): Traffic → Products → Revenue
              </h2>
            </Card.Header>
            <Card.Body className="p-0">
              {funnelData ? (
                <FunnelDiagram
                  products={funnelData.products}
                  upgradePaths={funnelData.upgrade_paths}
                  promotions={funnelData.promotions}
                  trafficSources={funnelData.traffic_sources}
                  contentVolume={(() => {
                    // Use recommended tier's content volumes from capacity analysis
                    const recommendedTier = capacityAnalysis?.recommendation?.tier;
                    const tierData = capacityAnalysis?.tier_analyses?.[recommendedTier]?.projection?.[0]?.content_volume;
                    if (tierData) {
                      return {
                        social: tierData.total_social,
                        videos: tierData.videos,
                        emails: tierData.emails,
                        articles: tierData.articles
                      };
                    }
                    // Fallback to battle plan monthly output
                    if (battlePlan?.monthly_output) {
                      return {
                        social: battlePlan.monthly_output.total_social,
                        videos: battlePlan.monthly_output.videos,
                        emails: battlePlan.monthly_output.emails,
                        articles: battlePlan.monthly_output.articles
                      };
                    }
                    return null;
                  })()}
                  salesTargets={battlePlan?.first_month_targets}
                />
              ) : (
                <div className="text-center py-5 text-muted">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading your funnel visualization...
                </div>
              )}
            </Card.Body>
            <Card.Footer className="bg-light">
              <small className="text-muted">
                This shows how traffic flows from your marketing channels into entry products, then upgrades through your <TextWithTooltips text="value ladder" />.
                {funnelData?.promotions?.length > 0 && (
                  <span className="text-danger ms-2">
                    <strong>{funnelData.promotions.length} promotion(s)</strong> scheduled in the next 30 days!
                  </span>
                )}
              </small>
            </Card.Footer>
          </Card>
        );
      })()}

      {/* First 30 Days Targets from Exit Strategy */}
      {battlePlan?.first_month_targets && (
        <Card className="mb-5 shadow border-primary" style={{ borderWidth: '2px' }}>
          <Card.Body className="p-4">
            <h3 className="mb-3">
              🎯 Your Month 1 Targets
              {battlePlan.first_month_targets.date_range_start && (
                <small className="text-muted ms-2" style={{ fontSize: '0.6em' }}>
                  ({battlePlan.first_month_targets.date_range_start} - {battlePlan.first_month_targets.date_range_end})
                </small>
              )}
            </h3>
            <p className="text-muted mb-4">
              Here's what needs to happen in the next 30 days to stay on track for your {battlePlan.first_month_targets.target_date || 'exit'} goal:
            </p>

            <Row>
              {/* Scheduled Promotions */}
              <Col md={4} className="mb-3">
                <Card className="h-100 border-warning">
                  <Card.Body>
                    <h5 className="text-warning mb-3">🎯 Scheduled Promotions</h5>
                    {battlePlan.first_month_targets.promo_notes && battlePlan.first_month_targets.promo_notes.length > 0 ? (
                      <ul className="mb-0">
                        {battlePlan.first_month_targets.promo_notes.map((note, index) => (
                          <li key={index} className="mb-2">{note}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted mb-0">No promotions scheduled this month</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Organic Acquisitions */}
              <Col md={4} className="mb-3">
                <Card className="h-100 border-success">
                  <Card.Body>
                    <h5 className="text-success mb-3">🌱 <TextWithTooltips text="Organic Acquisition" /> Targets</h5>
                    {battlePlan.first_month_targets.entry_acquisitions && Object.keys(battlePlan.first_month_targets.entry_acquisitions).length > 0 ? (
                      <ul className="mb-0">
                        {Object.entries(battlePlan.first_month_targets.entry_acquisitions).map(([productName, count], index) => (
                          <li key={index} className="mb-2">
                            <strong>{count}</strong> new customers for <strong>{productName}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted mb-0">No organic targets set</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Expected Upgrades */}
              <Col md={4} className="mb-3">
                <Card className="h-100 border-info">
                  <Card.Body>
                    <h5 className="text-info mb-3">⬆️ Expected Upgrades</h5>
                    {battlePlan.first_month_targets.upgrade_notes && battlePlan.first_month_targets.upgrade_notes.length > 0 ? (
                      <ul className="mb-0 small">
                        {battlePlan.first_month_targets.upgrade_notes.map((note, index) => (
                          <li key={index} className="mb-1">{note}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted mb-0">No upgrade projections</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Alert variant="info" className="mt-3 mb-0">
              <strong>💡 This is what the content plan below is designed to achieve.</strong> The weekly tasks in Month 1 are calibrated to drive these specific acquisition and upgrade targets.
            </Alert>

            <Alert variant="warning" className="mt-3 mb-0">
              <strong>⏰ Timing Note:</strong> These targets are calibrated for activation <strong>today</strong>. If you delay signing up, we'll help you recalibrate your plan when you activate the <TextWithTooltips text="Command Center" /> — checking which promotions happened, which need rescheduling, and adjusting organic targets accordingly.
            </Alert>
          </Card.Body>
        </Card>
      )}

      {/* FUNNEL ACTIVATION ROADMAP */}
      {battlePlan?.funnel_activation_plan?.length > 0 && (() => {
        const topPriority = battlePlan.funnel_activation_plan.find(s => s.month === 1);
        const remainingStages = battlePlan.funnel_activation_plan.filter(s => s.month !== 1);
        return (
        <Card className="mb-5 shadow-lg">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h4 className="mb-0">Your Funnel Activation Roadmap</h4>
          </Card.Header>
          <Card.Body className="p-4">
            <p className="text-muted mb-4">
              Based on your priority rankings, here's your month-by-month funnel activation plan. Focus on one stage per month for best results.
            </p>

            {/* Hero card for #1 priority */}
            {topPriority && (
              <Card
                className="mb-4"
                style={{
                  borderLeft: `4px solid ${colors.redDark}`,
                  borderTop: `1px solid ${colors.greyLight}`,
                  borderRight: `1px solid ${colors.greyLight}`,
                  borderBottom: `1px solid ${colors.greyLight}`,
                }}
              >
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center flex-wrap">
                    <div className="d-flex align-items-center me-4 mb-2">
                      <span style={{
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        color: colors.redDark,
                        lineHeight: 1,
                        marginRight: '0.75rem',
                      }}>&raquo;</span>
                      <span style={{
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        color: colors.redDark,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>Do This Now</span>
                    </div>
                    <div className="flex-grow-1 mb-2">
                      <h5 className="fw-bold mb-1">{topPriority.stage_name}</h5>
                      <p className="text-muted mb-1">{topPriority.description}</p>
                      <small className="text-muted">You ranked this as your #1 priority</small>
                    </div>
                    <div className="mb-2">
                      <Button
                        onClick={() => navigate(topPriority.link)}
                        style={{
                          background: gradients.redButton,
                          border: 'none',
                          fontWeight: 'bold',
                          padding: '0.5rem 1.5rem',
                          fontSize: '1rem',
                        }}
                      >
                        Get Started
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            <Row>
              {remainingStages.map((stage) => (
                <Col md={6} lg={4} key={stage.stage_key} className="mb-3">
                  <Card className="h-100 border" style={{ borderColor: colors.greyLight }}>
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-2">
                        <Badge
                          className="me-2"
                          style={{
                            background: stage.month <= 2 ? gradients.primary : gradients.success,
                            fontSize: '0.85rem',
                            padding: '0.4em 0.7em',
                          }}
                        >
                          Month {stage.month}
                        </Badge>
                      </div>
                      <h6 className="fw-bold mb-1">{stage.stage_name}</h6>
                      <p className="text-muted small mb-3 flex-grow-1">{stage.description}</p>
                      <Button
                        size="sm"
                        variant="dark"
                        onClick={() => navigate(stage.link)}
                      >
                        {stage.action_text}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <Alert variant="info" className="mt-3 mb-0">
              <strong>These milestones have been added to your Command Center calendar.</strong> You'll see them as monthly events to keep you on track.
            </Alert>
          </Card.Body>
        </Card>
        );
      })()}

      {/* TIER RECOMMENDATION based on Exit Strategy */}
      {capacityAnalysis && capacityAnalysis.recommendation && (
        <Card className="mb-5 shadow-lg border-primary" style={{ borderWidth: '2px' }}>
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h3 className="mb-0">
                <span className="me-2">🎯</span>
                Your Recommended <TextWithTooltips text="Command Center" /> Package
              </h3>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleDownloadCapacityCSV}
                title="Download detailed capacity analysis"
              >
                📊 Download Analysis
              </Button>
            </div>

            {/* "Black Tier" scenario for $50M+ exits that need custom strategies */}
            {capacityAnalysis.recommendation.needs_call ? (
              <Alert variant="dark" className="mb-4">
                <div className="d-flex align-items-center">
                  <div className="me-4">
                    <div className="text-muted small">Recommendation</div>
                    <div className="fw-bold fs-4 text-warning">Black Tier</div>
                    <div className="text-muted">Custom Growth Engine</div>
                  </div>
                  <div className="flex-grow-1">
                    <p className="mb-2">{capacityAnalysis.recommendation.reasoning}</p>
                    <Button variant="warning" href="mailto:strategy@aiprofitengineer.com?subject=Black%20Tier%20Inquiry%20-%20Custom%20Growth%20Strategy">
                      🖤 Schedule a Black Tier Consultation
                    </Button>
                  </div>
                </div>
              </Alert>
            ) : (
              /* Standard tier recommendation */
              <Alert variant={capacityAnalysis.recommendation.needs_profit_zone ? 'warning' : 'success'} className="mb-4">
                <div className="d-flex align-items-center">
                  <div className="me-4">
                    <div className="text-muted small">Recommended Tier</div>
                    <div className="fw-bold fs-4">{capacityAnalysis.recommendation.tier_name}</div>
                    <div className="text-muted">${capacityAnalysis.recommendation.price}/month</div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="text-muted small mb-1">Coverage of Your <TextWithTooltips text="Organic Acquisition" /> Needs</div>
                    <ProgressBar
                      now={Math.min(capacityAnalysis.recommendation.coverage_percentage, 100)}
                      label={`${capacityAnalysis.recommendation.coverage_percentage.toFixed(0)}%`}
                      style={{
                        height: '25px',
                        '--bs-progress-bar-bg': capacityAnalysis.recommendation.coverage_percentage >= 70 ? colors.navy : '#dc3545',
                      }}
                    />
                  </div>
                </div>
              </Alert>
            )}

            <p className="mb-3">{capacityAnalysis.recommendation.reasoning}</p>

            {capacityAnalysis.recommendation.needs_profit_zone && (
              <Alert variant="info" className="mb-3">
                <strong><TextWithTooltips text="Profit Zone" /> Partnership:</strong> Your exit goals are ambitious — and we love that!
                The {capacityAnalysis.recommendation.tier_name} tier handles your organic marketing, and our <TextWithTooltips text="Profit Zone" /> AI
                works alongside you to find the best accelerated strategies for <em>your</em> business — whether that's
                JV partnerships, affiliate programs, or strategic alliances. It'll help you implement them with
                step-by-step guidance and gentle nudges to keep everything on track. Together, we'll fill the gap
                of approximately {capacityAnalysis.recommendation.profit_zone_gap.toLocaleString()} additional customers.
              </Alert>
            )}

            {/* Tier Comparison - hide for "call us" scenario */}
            {!capacityAnalysis.recommendation.needs_call && (
            <>
            <h5 className="mt-4 mb-3">Package Comparison</h5>
            <Table striped bordered hover size="sm">
              <thead className="table-dark">
                <tr>
                  <th>Package</th>
                  <th>Price</th>
                  <th>Social Posts</th>
                  <th>Videos</th>
                  <th>Articles</th>
                  <th>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {['essential', 'advanced', 'ultimate'].map(tierKey => {
                  const tier = capacityAnalysis.tier_analyses[tierKey];
                  const isRecommended = tierKey === capacityAnalysis.recommendation.tier;
                  return (
                    <tr key={tierKey} className={isRecommended ? 'table-primary' : ''}>
                      <td>
                        <strong>{tier.tier_name}</strong>
                        {isRecommended && <Badge bg="primary" className="ms-2">Recommended</Badge>}
                      </td>
                      <td>${tier.price}/mo</td>
                      <td>{tier.projection?.[0]?.content_volume?.total_social || '-'}/mo</td>
                      <td>{tier.projection?.[0]?.content_volume?.videos || '-'}/mo</td>
                      <td>{tier.projection?.[0]?.content_volume?.articles || '-'}/mo</td>
                      <td>
                        <Badge bg={tier.coverage_percentage >= 100 ? 'success' : tier.coverage_percentage >= 70 ? 'warning' : 'danger'}>
                          {tier.coverage_percentage.toFixed(0)}%
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <p className="text-muted small mb-0">
              Coverage shows what percentage of your exit strategy's <TextWithTooltips text="organic acquisition" /> needs can be met through content marketing alone.
              Promotions and planned deals are already accounted for in your exit strategy.
            </p>
            </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ENHANCEMENT #2: Endowed Progress - Uncompleted Checkboxes */}
      <Card className="mb-5 shadow-sm">
        <Card.Body>
          <h3 className="mb-4">⏰ Your Setup will be Completed in MINUTES (4 Steps Remaining)</h3>
          <p className="text-muted mb-3">
            These setup items are automagically completed when you upgrade:
          </p>
          <style>{`
            .setup-checkbox .form-check-input:not(:checked) {
              background-color: #ffc107;
              border-color: #ffc107;
            }
          `}</style>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Check
                type="checkbox"
                id="setup-logo"
                className="setup-checkbox"
                checked={setupComplete.logo}
                onChange={() => toggleSetupItem('logo')}
                label={
                  <div>
                    <strong>GoHighLevel Account Unlocked and Integrated</strong>
                    <div className="text-muted small">Complimentary account unlocked, workflows automatically imported and customized</div>
                  </div>
                }
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Check
                type="checkbox"
                id="setup-ghl"
                className="setup-checkbox"
                checked={setupComplete.ghl}
                onChange={() => toggleSetupItem('ghl')}
                label={
                  <div>
                    <strong>Logo & Brand Assets Uploaded</strong>
                    <div className="text-muted small">Automatically imported and applied from your brand profile</div>
                  </div>
                }
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Check
                type="checkbox"
                id="setup-qr"
                className="setup-checkbox"
                checked={setupComplete.qr}
                onChange={() => toggleSetupItem('qr')}
                label={
                  <div>
                    <strong>Lead Magnet System Ready to Go</strong>
                    <div className="text-muted small">Sales Page Created and Branded, Branded PDF Uploaded and Ready to Go, Emails and Automation Imported</div>
                  </div>
                }
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Check
                type="checkbox"
                id="setup-email"
                className="setup-checkbox"
                checked={setupComplete.email}
                onChange={() => toggleSetupItem('email')}
                label={
                  <div>
                    <strong>New Sales Page and Welcome Emails Locked and Loaded</strong>
                    <div className="text-muted small">Sales Page Configured, Branded and Live, Welcome Email Autoresponders queued and ready</div>
                  </div>
                }
              />
            </Col>
          </Row>
          <Alert variant="info" className="mt-3 mb-0">
            <strong>📱 One Quick Thing:</strong> You'll need to connect your social media accounts and calendar to GoHighLevel yourself (we can't access your login credentials). Don't worry - it only takes a few minutes and we'll walk you through it!
          </Alert>
          {Object.values(setupComplete).every(v => v) && (
            <Alert variant="success" className="mt-3 mb-0">
              ✓ All setup tasks complete! You're ready to execute your battle plan.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Month 1: Weekly Breakdown */}
      <Card className="mb-5 shadow">
        <Card.Header className="bg-primary text-white">
          <h2 className="mb-0">📅 Month 1: Week-by-Week Action Plan</h2>
        </Card.Header>
        <Card.Body>
          <p className="lead mb-4">
            Your first 30 days focus on <strong>establishing consistent presence</strong> across key channels.
            Each week includes content creation, distribution, and quality checks.
          </p>

          {battlePlan.month_1_weeks.map((week) => (
            <Card key={week.week_number} className="mb-4" style={{ borderLeft: '4px solid #0d6efd' }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">Week {week.week_number}</h4>
                  <Badge bg="secondary" style={{ fontSize: '1rem' }}>
                    {week.total_hours.toFixed(1)} hours total
                  </Badge>
                </div>

                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '30%' }}>Task</th>
                      <th style={{ width: '10%' }}>Qty</th>
                      <th style={{ width: '10%' }}>Time</th>
                      <th style={{ width: '15%' }}>Stage</th>
                      <th style={{ width: '35%' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {week.tasks.map((task, idx) => (
                      <tr key={idx}>
                        <td><strong>{task.task}</strong></td>
                        <td>{task.quantity}</td>
                        <td>{task.time_hours.toFixed(1)}h</td>
                        <td>{getFunnelStageBadge(task.funnel_stage)}</td>
                        <td className="text-muted">{task.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ))}

          {/* ENHANCEMENT #4: Precise Numbers with Dollar Value */}
          <Card className="bg-light">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">📊 Month 1 Total Output</h5>
                {battlePlan.monthly_output.tier_name && (
                  <Badge bg="primary" style={{ fontSize: '0.9rem' }}>
                    {battlePlan.monthly_output.tier_name} Tier - ${battlePlan.monthly_output.tier_price}/mo
                  </Badge>
                )}
              </div>
              <Row className="text-center">
                <Col md={2}>
                  <div className="fw-bold" style={{ fontSize: '2rem', color: '#0d6efd' }}>
                    {battlePlan.monthly_output.total_social || battlePlan.monthly_output.social_posts}
                  </div>
                  <div className="text-muted">Social Posts</div>
                  {battlePlan.monthly_output.reposts > 0 && (
                    <div className="small text-muted">({battlePlan.monthly_output.social_posts} new + {battlePlan.monthly_output.reposts} reposts)</div>
                  )}
                </Col>
                <Col md={2}>
                  <div className="fw-bold" style={{ fontSize: '2rem', color: '#0d6efd' }}>
                    {battlePlan.monthly_output.videos}
                  </div>
                  <div className="text-muted">Videos</div>
                </Col>
                <Col md={2}>
                  <div className="fw-bold" style={{ fontSize: '2rem', color: '#0d6efd' }}>
                    {battlePlan.monthly_output.emails}
                  </div>
                  <div className="text-muted">Emails</div>
                </Col>
                <Col md={2}>
                  <div className="fw-bold" style={{ fontSize: '2rem', color: '#0d6efd' }}>
                    {battlePlan.monthly_output.articles}
                  </div>
                  <div className="text-muted">Articles</div>
                </Col>
                <Col md={2}>
                  <div className="fw-bold" style={{ fontSize: '2rem', color: '#0d6efd' }}>
                    {battlePlan.monthly_output.audits}
                  </div>
                  <div className="text-muted">Audits</div>
                </Col>
                <Col md={2}>
                  <div className="fw-bold" style={{ fontSize: '2rem', color: '#198754' }}>
                    {preciseHours.toFixed(1)}
                  </div>
                  <div className="text-muted">Hours Saved/Month</div>
                  <div className="small text-success fw-bold">(${dollarValue.toLocaleString()} value at $45/hr)</div>
                  {battlePlan.monthly_output.goodies_hours > 0 && (
                    <div className="small text-muted mt-1">
                      {battlePlan.monthly_output.content_creation_hours?.toFixed(1) || '0'} content +{' '}
                      {battlePlan.monthly_output.goodies_hours} extras
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Month 2-3 Roadmap */}
      <Row className="mb-5">
        <Col md={6}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-success text-white">
              <h3 className="mb-0">📈 Month 2</h3>
            </Card.Header>
            <Card.Body>
              <div dangerouslySetInnerHTML={{ __html: battlePlan.month_2_roadmap.replace(/\n/g, '<br />') }} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-warning text-dark">
              <h3 className="mb-0">🚀 Month 3</h3>
            </Card.Header>
            <Card.Body>
              <div dangerouslySetInnerHTML={{ __html: battlePlan.month_3_roadmap.replace(/\n/g, '<br />') }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Funnel Stages Education - HIDDEN FOR NOW - needs redesign
      <Card className="mb-5 shadow">
        <Card.Header className="bg-info text-white">
          <h2 className="mb-0">🎯 Understanding Your Customer Journey</h2>
        </Card.Header>
        <Card.Body>
          <p className="lead mb-4">
            Your content strategy maps to different stages of how customers discover, evaluate, and choose you:
          </p>

          <Row>
            {Object.entries(battlePlan.funnel_stages).map(([key, stage]) => {
              // Map technical names to user-friendly labels
              const friendlyNames = {
                'Top of Funnel (TOFU)': 'Attract Stage',
                'Middle of Funnel (MOFU)': 'Engage Stage',
                'Bottom of Funnel (BOFU)': 'Convert Stage',
                'Retention & Advocacy': 'Retain & Grow Stage'
              };
              const displayName = friendlyNames[stage.name] || stage.name;

              return (
                <Col md={6} lg={3} key={key} className="mb-4">
                  <Card className="h-100 border-2" style={{ borderColor: '#17a2b8' }}>
                    <Card.Img
                      variant="top"
                      src={`http://localhost:8000${stage.diagram}`}
                      alt={displayName}
                      style={{ height: '150px', objectFit: 'cover' }}
                    />
                    <Card.Body>
                      <h5 className="text-info">{displayName}</h5>
                      <div className="mb-2">
                        <strong>Goal:</strong> {stage.goal}
                      </div>
                      <div className="mb-2">
                        <strong>Content:</strong>
                        <div className="text-muted small">{stage.content_types}</div>
                      </div>
                      <div className="text-muted small">{stage.description}</div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>
      */}

      {/* ENHANCEMENT #5: Cost Comparison with Decoy Effect */}
      <Card className="mb-5 shadow">
        <Card.Header className="bg-dark text-white">
          <h2 className="mb-0">💰 Investment Comparison</h2>
        </Card.Header>
        <Card.Body>
          <p className="lead mb-4">
            Compare the cost, time, and quality of different approaches to achieving the same monthly output:
          </p>

          <Table responsive bordered hover>
            <thead className="table-light">
              <tr>
                <th>Approach</th>
                <th>Setup Cost</th>
                <th>Monthly Cost</th>
                <th>Your Time</th>
                <th>Quality</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(battlePlan.cost_comparison)
                .sort(([, a], [, b]) => b.monthly_cost - a.monthly_cost)  // Sort by cost descending
                .map(([key, option]) => {
                const isAIProfitEngineer = key === 'ai_profit_engineer';
                return (
                  <tr key={key} className={isAIProfitEngineer ? 'table-success' : ''}>
                    <td>
                      <strong>{option.name}</strong>
                      {isAIProfitEngineer && <Badge bg="success" className="ms-2">✨ Best Value</Badge>}
                      <div className="text-muted small mt-1">{option.notes}</div>
                    </td>
                    <td>
                      <strong>${option.setup_cost.toLocaleString()}</strong>
                    </td>
                    <td>
                      <strong>${option.monthly_cost.toLocaleString()}</strong>
                    </td>
                    <td className="text-muted small">{option.time_overhead}</td>
                    <td className="text-muted small">{option.quality}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {/* Value Proposition */}
          <Card className="bg-light mt-4">
            <Card.Body>
              <h5 className="mb-3">Why AI Profit Engineer Wins</h5>
              <Row>
                <Col md={4}>
                  <div className="text-center mb-3">
                    <div className="fw-bold text-success" style={{ fontSize: '1.5rem' }}>
                      ${battlePlan.monthly_output.tier_price?.toLocaleString() || '497'}/month
                    </div>
                    <div className="text-muted">
                      vs. ${Math.min(...Object.entries(battlePlan.cost_comparison)
                        .filter(([k]) => k !== 'ai_profit_engineer')
                        .map(([, v]) => v.monthly_cost)).toLocaleString()}-${Math.max(...Object.entries(battlePlan.cost_comparison)
                        .filter(([k]) => k !== 'ai_profit_engineer')
                        .map(([, v]) => v.monthly_cost)).toLocaleString()} with alternatives
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center mb-3">
                    <div className="fw-bold text-success" style={{ fontSize: '1.5rem' }}>2-3 hours/month</div>
                    <div className="text-muted">vs. 5-15+ hours managing others</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center mb-3">
                    <div className="fw-bold text-success" style={{ fontSize: '1.5rem' }}>Brand-Aligned</div>
                    <div className="text-muted">Consistent quality, your voice, your strategy</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Included Features - What You Get Beyond Content */}
          {battlePlan.included_features && battlePlan.included_features.length > 0 && (
            <Card className="mt-4 border-success" style={{ borderWidth: '2px' }}>
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">✨ Also Included (All Packages)</h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">
                  Beyond content creation, here's what you'd have to do yourself (or pay for separately):
                </p>
                <Table responsive size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Feature</th>
                      <th>What It Does</th>
                      <th>Your Package</th>
                      <th className="text-center">DIY Hours/Mo</th>
                      <th>DIY Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {battlePlan.included_features.map((feature, idx) => (
                      <tr key={idx} className={feature.note ? 'table-light' : ''}>
                        <td><strong>{feature.name}</strong></td>
                        <td className="text-muted small">{feature.description}</td>
                        <td><Badge bg="success">{feature.tier_specific}</Badge></td>
                        <td className="text-danger text-center fw-bold">
                          {feature.monthly_hours_low ? `${feature.monthly_hours_low}+` : feature.diy_time}
                        </td>
                        <td className="text-danger">{feature.diy_cost}</td>
                      </tr>
                    ))}
                    <tr className="table-warning fw-bold">
                      <td colSpan={3} className="text-end">Total DIY Time (minimum):</td>
                      <td className="text-danger text-center" style={{ fontSize: '1.1rem' }}>
                        {battlePlan.included_features.reduce((sum, f) => sum + (f.monthly_hours_low || 0), 0)}+ hrs/month
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
                <Alert variant="danger" className="mb-0 mt-3">
                  <strong>⚠️ Reality Check:</strong> That's{' '}
                  <strong>
                    {battlePlan.included_features.reduce((sum, f) => sum + (f.monthly_hours_low || 0), 0)}+ hours/month
                  </strong>{' '}
                  just on behind-the-scenes work — <em>before</em> you create a single piece of content!
                  Combined with content creation ({battlePlan.monthly_output?.content_creation_hours?.toFixed(0) || '40'}+ hours),
                  you're looking at <strong className="text-danger" style={{ fontSize: '1.1rem' }}>
                    {(battlePlan.monthly_output?.content_creation_hours || 40) +
                     battlePlan.included_features.reduce((sum, f) => sum + (f.monthly_hours_low || 0), 0)}+ hours/month
                  </strong> if you DIY everything.
                </Alert>
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>

      {/* ENHANCEMENT #7: Present Bias - Instant Gratification */}
      <Card className="mb-5 shadow-lg border-warning" style={{ borderWidth: '3px' }}>
        <Card.Body>
          <h3 className="text-center mb-4">⚡ Instant Activation Benefits</h3>
          <p className="text-center lead mb-4">
            Activate the <TextWithTooltips text="Command Center" /> → Complete onboarding → Instantly unlock a full content arsenal ready for review and deploy.
          </p>
          {battlePlan.month_1_weeks?.[0]?.tasks && (
            <Row className="justify-content-center">
              <Col md={10} lg={8}>
                <Table className="mb-0">
                  <tbody>
                    {battlePlan.month_1_weeks[0].tasks
                      .filter(t => t.task !== 'Review and approve content')
                      .map((task, idx) => {
                        const deliverableNames = {
                          'Create and schedule social media posts': 'Social Media Posts',
                          'Produce short-form video content': 'Short-form Video Content',
                          'Draft and send email campaigns': 'Emails',
                          'Write and publish long-form content': 'Long-form Content',
                          'Re-share top-performing content': 'Re-shared Top-performing Posts',
                          'Curate industry content with commentary': "Curated Industry Leader's Content with Commentary",
                          'Social media performance audit': 'Social Media Performance Audit',
                          'SEO & website rank audit': 'SEO & Website Rank Audit',
                        };
                        return (
                          <tr key={idx}>
                            <td className="text-end pe-3" style={{ width: '60px' }}>
                              <strong className="text-warning" style={{ fontSize: '1.3rem' }}>{task.quantity}</strong>
                            </td>
                            <td>{deliverableNames[task.task] || task.task}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </Col>
            </Row>
          )}
          <div className="text-center mt-4 pt-3" style={{ borderTop: '2px solid #ffc107' }}>
            <strong>All generated in your first session — then the system keeps going week after week</strong>
          </div>
        </Card.Body>
      </Card>

      {/* ENHANCEMENT #6 & #8: Implementation Intention + Zeigarnik Effect */}
      <Card className="mb-5 shadow-lg border-0" style={{ background: `linear-gradient(135deg, #667eea 0%, ${colors.navyDark} 100%)` }}>
        <Card.Body className="text-white py-5">
          <div className="text-center mb-4">
            <h2 className="display-5 fw-bold mb-3">Your Battle Plan is 78% Complete</h2>
            <div className="mb-4">
              <Badge bg="light" text="dark" className="me-2 p-3">✓ Strategy Defined</Badge>
              <Badge bg="light" text="dark" className="me-2 p-3">✓ Timeline Mapped</Badge>
              <Badge bg="light" text="dark" className="me-2 p-3">✓ ROI Calculated</Badge>
              <Badge bg="warning" text="dark" className="p-3">⏳ Execution Engine: PENDING</Badge>
            </div>
            <Alert variant="warning" className="text-dark mb-4">
              <strong>⚠️ Plans without activation systems fail 9/10 times.</strong>
              <br />
              <small>(University of Scranton: 92% of goals fail at execution stage)</small>
            </Alert>
          </div>

          <h3 className="text-center mb-4">Choose Your Implementation Path:</h3>

          <Row className="mb-4">
            <Col md={6} className="mb-3">
              <Card className="h-100 border-0 shadow">
                <Card.Body>
                  <h4 className="text-secondary mb-3">Path A: Manual Execution</h4>
                  <ul className="mb-4">
                    <li>Excel template with all 90 tasks</li>
                    <li>Time-blocking calendar spreadsheet</li>
                    <li>DIY content templates and checklists</li>
                    <li>47-hour time investment per month</li>
                    <li>Self-managed quality control</li>
                  </ul>
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    className="w-100 py-3 fw-bold"
                    onClick={handlePrint}
                  >
                    DOWNLOAD WORKSHEET →
                  </Button>
                  <div className="text-center mt-2 small" style={{ opacity: 0.8 }}>
                    Print and execute manually (good luck!)
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-3">
              <Card className="h-100 border-0 shadow">
                <Card.Body>
                  <h4 className="text-primary mb-3">Path B: Activate <TextWithTooltips text="Command Center" /></h4>
                  <ul className="mb-4">
                    <li>AI generates your Week 1 content in next 8 minutes</li>
                    <li><TextWithTooltips text="Content calendar" /> auto-populates with your <TextWithTooltips text="posting schedule" /></li>
                    <li>First audit runs automatically on Day 30</li>
                    <li>Human review quality-checks everything</li>
                    <li>Strategy adjusts based on real performance data</li>
                  </ul>
                  <Button
                    size="lg"
                    className="w-100 py-3 fw-bold btn-brand-primary"
                    onClick={commandCenterActivated ? () => navigate('/command-center') : handleUpgradeToCommandCenter}
                    disabled={upgrading}
                  >
                    {upgrading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Activating...
                      </>
                    ) : commandCenterActivated ? (
                      '✓ GO TO COMMAND CENTER →'
                    ) : (
                      'ACTIVATE COMMAND CENTER →'
                    )}
                  </Button>
                  <div className="text-center mt-2 small text-muted">
                    <strong>{commandCenterActivated ? 'Click to continue to your Command Center' : 'Upgrade in the next 24 hours and save 50% on the first 3 months'}</strong>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="text-center mt-4 pt-4" style={{ borderTop: '2px solid rgba(255,255,255,0.3)' }}>
            <p className="lead mb-0">
              <strong>The next 48 hours determine whether this becomes your REALITY or another PDF in your downloads folder.</strong>
            </p>
          </div>
        </Card.Body>
      </Card>

      {/* Print-only footer */}
      <div className="d-none d-print-block mt-5 text-center text-muted">
        <hr />
        <p>Generated by AI Profit Engineer | {new Date(battlePlan.generated_at).toLocaleDateString()}</p>
        <p>Visit aiprofitengineer.com to automate this entire plan</p>
        <p className="fw-bold">Remember: 92% of plans fail without an execution system. Don't be a statistic.</p>
      </div>

      {/* Coming Soon Modal */}
      <Modal show={showComingSoonModal} onHide={() => setShowComingSoonModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Coming Soon!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '3rem' }}>🚀</div>
          <h4 className="mt-3">The Command Center is Coming Soon!</h4>
          <p className="text-muted">
            We're putting the finishing touches on your AI-powered execution engine.
            Stay tuned for the launch!
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-brand-primary" onClick={() => setShowComingSoonModal(false)}>
            Got it!
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default BattlePlan;
